import { useAppDispatch, useAppTranslation } from "@/utils";
import { Alert, Button, Card, Flex, Typography, useMessagedAsync, useMessagedAsyncFn } from "@payping/web-components";
import { moment } from "@payping/utils/dateUtils";
import { bnplConsumerPromissoryGetInfo } from "@payping/redux/actions";
import { bnplConsumerApis } from "@payping/api";
import type { SDKRequestInit } from "@payping/api";
import LoadingScreen from "@/pages/loading";
import { dashboardReduxReducerActions } from "@/redux/actions/dashboard";
import { RetryGetOrderSteps } from "@/redux/reducers/dashboard";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import PayGetOrderUnAvailable from "../UnAvailable";
import PayGetOrderIssuePromissoryFailed from "./Failed";
import PayExpiredOrder from "../../ExpiredOrder";
import PayUnAvailable from "../../Unavailable";
import PayGetOrderLayout from "../Layout";

interface IssuePromissoryProps {
  trackingCode: string;
  afterSuccess: () => void;
}
const PayGetOrderIssuePromissory = ({ afterSuccess, trackingCode }: IssuePromissoryProps) => {
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();

  const getPromissoryState = useMessagedAsync(
    () => dispatch(bnplConsumerPromissoryGetInfo(trackingCode, { isRaw: true } as SDKRequestInit)),
    [dispatch, trackingCode],
    {
      success: false,
      error: true,
    },
  );

  const [requestPromissoryState, requestPromissory] = useMessagedAsyncFn(async () => {
    try {
      const res = await bnplConsumerApis.promissoryIssue({ trackingCode }, {
        isRaw: true,
      } as SDKRequestInit);
      return res;
    } catch (e: any) {
      if (e.status === 400) {
        try {
          const parsedError = await e.json();
          throw parsedError;
        } catch (jsonError: any) {
          if (jsonError.status === 400) {
            throw jsonError;
          }
          throw e; // Fallback if parsing JSON fails
        }
      }
      throw e;
    }
  }, [trackingCode]);

  const handleRequestPromissory = async () => {
    const res = await requestPromissory();
    // paid before
    if (res.isDone) {
      afterSuccess();

      return;
    }

    if (res.data) {
      window.location.replace(res.data);
    }
  };

  // expired order
  if ((requestPromissoryState.error as any)?.key === 109) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayExpiredOrder />
      </PayGetOrderLayout>
    );
  }

  // error in generate issue
  if ((requestPromissoryState.error as any)?.key === 106 && (requestPromissoryState.error as any)?.detail) {
    return (
      <PayGetOrderLayout showHeaders currentStepError>
        <PayGetOrderIssuePromissoryFailed
          onRetry={() => {
            dispatch(dashboardReduxReducerActions.setRetryGetOrder(RetryGetOrderSteps.startGetOrder));
          }}
          message={(requestPromissoryState.error as any)?.detail}
        />
      </PayGetOrderLayout>
    );
  }

  if (
    requestPromissoryState.error?.name === "AuthError" ||
    getPromissoryState.error?.name === "AuthError" ||
    getPromissoryState.loading
  ) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <LoadingScreen />
      </PayGetOrderLayout>
    );
  }

  // error
  if (getPromissoryState.error) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayUnAvailable
          onRetry={() => {
            dispatch(dashboardReduxReducerActions.setRetryGetOrder(RetryGetOrderSteps.startGetOrder));
          }}
        />
      </PayGetOrderLayout>
    );
  }

  if ((requestPromissoryState.error as any)?.status === 400) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayGetOrderUnAvailable
          errorMessages={(requestPromissoryState.error as any).errors ?? []}
          onRetry={handleRequestPromissory}
          onRetryLoading={requestPromissoryState.loading}
        />
      </PayGetOrderLayout>
    );
  }

  if (requestPromissoryState.error) {
    return (
      <PayGetOrderLayout showHeaders currentStepError>
        <PayGetOrderIssuePromissoryFailed
          onRetry={handleRequestPromissory}
          onRetryLoading={requestPromissoryState.loading}
        />
      </PayGetOrderLayout>
    );
  }

  return (
    <>
      {/* {contextHolder} */}
      <PayGetOrderLayout showHeaders>
        <Flex vertical className="tw:h-full tw:w-full tw:px-4! tw:pb-4! tw:pt-6!" gap={"large"}>
          <Flex gap={"small"} vertical>
            <Typography.Title level={5} className="tw:m-0! tw:font-semibold">
              {t("pay.getOrder.steps.issuePromissory.title")}
            </Typography.Title>
            <Typography.Paragraph>{t("pay.getOrder.steps.issuePromissory.description")}</Typography.Paragraph>
          </Flex>

          <Flex vertical className="tw:w-full">
            <Card size="small">
              <Flex vertical className="tw:w-full" gap={"32px"}>
                <Flex vertical className="tw:w-full" gap={"middle"}>
                  <Typography.Title level={5}>
                    {t("pay.getOrder.steps.issuePromissory.pre.issuePromissoryInfo")}
                  </Typography.Title>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.amount")}
                    </Typography.Text>
                    <Flex>
                      <Typography.Text>{`${formatLocalizedNumber({ value: getPromissoryState.value?.amount ?? 0 })}`}</Typography.Text>
                      <CuToman width={24} height={24} />
                    </Flex>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.dueDate")}
                    </Typography.Text>
                    <Typography.Text>
                      {moment(getPromissoryState.value?.dueDate).format("jYYYY/jMM/jDD")}
                    </Typography.Text>
                  </Flex>
                </Flex>
                <Flex vertical className="tw:w-full" gap={"middle"}>
                  <Typography.Title level={5}>
                    {t("pay.getOrder.steps.issuePromissory.pre.issuerInformation")}
                  </Typography.Title>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.fullName")}
                    </Typography.Text>
                    <Typography.Text>{getPromissoryState.value?.consumerFullName}</Typography.Text>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.nationalCode")}
                    </Typography.Text>
                    <Typography.Text>{getPromissoryState.value?.consumerNationalCode}</Typography.Text>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.mobile")}
                    </Typography.Text>
                    <Typography.Text>{getPromissoryState.value?.consumerMobile}</Typography.Text>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Paragraph className="tw:m-0! tw:min-w-12" type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.address")}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="tw:m-0! tw:max-w-xs tw:overflow-hidden tw:truncate tw:whitespace-nowrap">
                      {getPromissoryState.value?.consumerAddress}
                    </Typography.Paragraph>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.postalCode")}
                    </Typography.Text>
                    <Typography.Text>{getPromissoryState.value?.consumerPostalCode}</Typography.Text>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.shebaNumber")}
                    </Typography.Text>
                    <Typography.Text>{getPromissoryState.value?.consumerIBan}</Typography.Text>
                  </Flex>
                </Flex>
                <Flex vertical className="tw:w-full" gap={"middle"}>
                  <Typography.Title level={5}>
                    {t("pay.getOrder.steps.issuePromissory.pre.recipientInformation")}
                  </Typography.Title>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.fullName")}
                    </Typography.Text>
                    <Typography.Text>{getPromissoryState.value?.merchantFullName}</Typography.Text>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.nationalCode")}
                    </Typography.Text>
                    <Typography.Text>{getPromissoryState.value?.merchantNationalCode}</Typography.Text>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.mobile")}
                    </Typography.Text>
                    <Typography.Text>{getPromissoryState.value?.merchantMobile}</Typography.Text>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Paragraph className="tw:m-0! tw:min-w-12" type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.address")}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="tw:m-0! tw:max-w-xs tw:overflow-hidden tw:truncate tw:whitespace-nowrap">
                      {getPromissoryState.value?.merchantAddress}
                    </Typography.Paragraph>
                  </Flex>
                  <Flex className="tw:w-full" align="center" justify="space-between">
                    <Typography.Text type="secondary">
                      {t("pay.getOrder.steps.issuePromissory.pre.postalCode")}
                    </Typography.Text>
                    <Typography.Text>{getPromissoryState.value?.merchantPostalCode}</Typography.Text>
                  </Flex>
                </Flex>
              </Flex>
            </Card>
          </Flex>
          {getPromissoryState.value?.hasPromissoryPayment ? (
            <Alert
              title={t("pay.getOrder.steps.issuePromissory.stampDutyContentAlert", {
                amount: `${formatLocalizedNumber({ value: getPromissoryState.value.promissoryAmount ?? 0 })} ${t("common.tomans")}`,
              })}
              type="info"
              showIcon
            />
          ) : null}
          <Button
            className="tw:mt-auto"
            htmlType="submit"
            type="primary"
            block
            loading={requestPromissoryState.loading}
            onClick={handleRequestPromissory}
          >
            {t("common.confirmAndContinue")}
          </Button>
        </Flex>
      </PayGetOrderLayout>
    </>
  );
};

export default PayGetOrderIssuePromissory;
